import { ArrowLeft20 } from '@carbon/icons-react';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Pagination, Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Button } from 'components/atoms/button';
import {
  CpBody03Secondary,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { CpSectionWrapper as CpSectionWrapperAtom } from '../../common';
import { CpImageGalleryVariantWidgetProps } from '../types';

export const CpImageGalleryCarouselWrapper = styled.div<{ areImagesAvailable }>`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  overflow-y: hidden;
  gap: var(--cp-spacing-06);

  @media (max-width: 480px) {
    justify-content: flex-start;
    align-items: flex-start;
  }

  .controlButton {
    @media (max-width: 480px) {
      display: none;
    }
  }

  .controlButtonMinimized {
    display: none;
    @media (max-width: 480px) {
      display: flex;
    }
  }

  .swiper {
    width: 85%;
    height: 50%;
    margin: 0 var(--cp-spacing-06);
    margin-top: var(--cp-spacing-08);

    @media (max-width: 768px) {
      width: 80%;
    }

    @media (max-width: 480px) {
      width: 100%;
      margin: 0;
    }
  }

  .swiper-slide {
    text-align: center;
    font-size: 18px;
    background: #fff;

    /* Center slide text vertically */
    display: flex;
    justify-content: center;
    align-items: center;
    /* margin: auto; */
    height: auto;
  }

  .swiper-slide .ant-skeleton,
  .swiper-slide img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  .swiper-pagination-bullets {
    margin-top: var(--cp-spacing-06);
    display: ${({ areImagesAvailable }) =>
      areImagesAvailable ? 'flex' : 'none'};
    justify-content: center;
    gap: var(--cp-spacing-03);

    @media (max-width: 480px) {
      justify-content: flex-start;
      align-items: flex-start;
      margin-top: var(--cp-spacing-09);
    }
  }

  .swiper-pagination-bullet {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--cp-background-as-black-opacity);
    cursor: pointer !important;
  }

  .swiper-pagination-bullet-active {
    background-color: black;
  }

  .container {
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #eeeeee;
  }

  .content-box {
    width: 1800px;
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .card {
    width: 200px;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card p {
    font-size: 70px;
    font-weight: 600;
    color: #ffffff;
    opacity: 0.4;
  }

  .action-btns {
    display: flex;
    align-items: center;
    gap: 60px;
    margin-top: 50px;
  }

  .action-btns button {
    font-size: 15px;
    font-weight: 500;
    color: #002d36;
    background-color: #99c6cf;
    border-radius: 6px;
    padding: 10px 25px;
    border: none;
    cursor: pointer !important;
    transition: all 0.5s ease;
  }

  .action-btns button:hover {
    color: #ffffff;
    background-color: #002d36;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  justify-content: center;
  align-items: center;

  @media (max-width: 480px) {
    justify-content: flex-start;
    align-items: flex-start;
    gap: var(--cp-spacing-03);
  }
`;

const Title = styled(CpHeading03Primary)`
  margin-bottom: 0;
`;

const Description = styled(CpBody03Secondary)`
  margin-bottom: 0;
`;

export const CarouselWithControls = styled.div<{ areImagesAvailable }>`
  width: 100%;
  display: ${({ areImagesAvailable }) =>
    areImagesAvailable ? 'flex' : 'none'};
  justify-content: space-between;
  align-items: center;
`;

export const ControlButton = styled(Button)`
  border-radius: 50%;
  color: var(--cp-icon-primary);
  border-color: var(--cp-icon-color-disabled);
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  background-color: transparent;
  margin: 4px;
  cursor: pointer !important;

  @media (max-width: 480px) {
    margin: 0;
  }

  .ant-btn:hover,
  .ant-btn:active {
    color: var(--cp-icon-primary) !important;
    border-color: var(--cp-icon-color-disabled) !important;
    background-color: transparent !important;
  }

  ::after {
    display: none;
  }
`;

const CpSectionWrapper = styled(CpSectionWrapperAtom)`
  position: relative;
`;

export const ControlsAtBottom = styled.div`
  gap: 24px;
  position: absolute;
  display: flex;
  left: 14rem;
  top: 27.5rem;
  z-index: 10;
`;

export const RightArrow = styled(ArrowLeft20)`
  transform: scaleX(-1);
`;

export const CpImageGalleryCarousel: React.FC<CpImageGalleryVariantWidgetProps> =
  ({ disabled, data, widget }) => {
    const background = useGetBackground(widget);

    const [current, setCurrent] = useState(0);
    const [swiperRef, setSwiperRef] = useState<any>();

    const handlePrevious = () => {
      let slideTo;

      if (current - 1 === -1) {
        slideTo = data?.images.length - 1;
      } else {
        slideTo = current - 1;
      }

      setCurrent(() => slideTo);
      swiperRef?.slideTo(slideTo);
    };

    const handleNext = () => {
      let slideTo;

      if (current + 1 === data?.images.length) {
        slideTo = 0;
      } else {
        slideTo = current + 1;
      }

      setCurrent(() => slideTo);
      swiperRef?.slideTo(slideTo);
    };

    const areImagesAvailable = data?.images?.length > 0;

    return (
      <CpSectionWrapper background={background} isDisabled={disabled}>
        <CpImageGalleryCarouselWrapper areImagesAvailable={areImagesAvailable}>
          <TitleWrapper>
            <Title autoAdjustColor data-cy="cp.image_gallery_carousel.title">
              {data?.title}
            </Title>
            <Description
              autoAdjustColor
              data-cy="cp.image_gallery_carousel.description"
            >
              {data?.description}
            </Description>
          </TitleWrapper>
          <CarouselWithControls areImagesAvailable={areImagesAvailable}>
            <ControlButton
              className="controlButton"
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
              onClick={handlePrevious}
            />
            <Swiper
              onSwiper={setSwiperRef}
              modules={[Pagination, Autoplay]}
              pagination={{ clickable: true }}
              className="mySwiper"
              speed={500}
              spaceBetween={50}
              autoplay={{
                delay: 2000,
                disableOnInteraction: true,
              }}
            >
              {data?.images?.map((img, index) => (
                <SwiperSlide>
                  <img
                    data-cy={`cp.image_gallery_carousel.image.${index}`}
                    src={img.attachment.location}
                    alt={img.attachment.name}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <ControlButton
              className="controlButton"
              icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
              onClick={handleNext}
            />
          </CarouselWithControls>
          <ControlsAtBottom>
            <ControlButton
              className="controlButtonMinimized"
              icon={<ArrowLeft20 className="cp-svg-auto-adjust-fill" />}
              onClick={handlePrevious}
            />
            <ControlButton
              className="controlButtonMinimized"
              icon={<RightArrow className="cp-svg-auto-adjust-fill" />}
              onClick={handleNext}
            />
          </ControlsAtBottom>
        </CpImageGalleryCarouselWrapper>
      </CpSectionWrapper>
    );
  };
